import { http } from './http'
import { ENDPOINTS } from './endpoints'
import type { ApiResponse } from './http/types'
import type {
  WorkspaceState,
  StoreThemeDraft,
  PreviewToken,
  CreateDraftDto,
  PatchStyleDto,
  PatchBrandingDto,
  PatchSectionContentDto,
  PatchSectionSettingsDto,
  PatchSectionVisibilityDto,
  SwapSectionVariantDto,
  AddSectionDto,
  ReorderSectionsDto,
  SendMessageDto,
  TranscriptionResult,
  ConfirmThemeAssistantProposalDto,
  ThemeAssistantProposalResult,
  WorkspaceHistoryState,
} from '@/types/theme-workspace'

export const themeWorkspaceService = {
  /* Workspace state */
  get: (storeId: string) =>
    http.get<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.base(storeId)),

  /* Draft lifecycle */
  createDraft: (storeId: string, dto: CreateDraftDto) =>
    http.post<ApiResponse<StoreThemeDraft>>(ENDPOINTS.themeWorkspace.draft(storeId), dto),

  apply: (storeId: string) =>
    http.post<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.apply(storeId)),

  reset: (storeId: string) =>
    http.post<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.reset(storeId)),

  publish: (storeId: string) =>
    http.post<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.publish(storeId)),

  previewToken: (storeId: string) =>
    http.post<ApiResponse<PreviewToken>>(ENDPOINTS.themeWorkspace.previewToken(storeId)),

  /* Global mutations */
  patchStyle: (storeId: string, dto: PatchStyleDto) =>
    http.patch<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.style(storeId), dto),

  patchBranding: (storeId: string, dto: PatchBrandingDto) =>
    http.patch<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.branding(storeId), dto),

  /* Section mutations */
  patchSectionContent: (storeId: string, sectionId: string, dto: PatchSectionContentDto) =>
    http.patch<ApiResponse<WorkspaceState>>(
      ENDPOINTS.themeWorkspace.sectionContent(storeId, sectionId),
      dto,
    ),

  patchSectionSettings: (storeId: string, sectionId: string, dto: PatchSectionSettingsDto) =>
    http.patch<ApiResponse<WorkspaceState>>(
      ENDPOINTS.themeWorkspace.sectionSettings(storeId, sectionId),
      dto,
    ),

  patchSectionVisibility: (storeId: string, sectionId: string, dto: PatchSectionVisibilityDto) =>
    http.patch<ApiResponse<WorkspaceState>>(
      ENDPOINTS.themeWorkspace.sectionVisibility(storeId, sectionId),
      dto,
    ),

  swapSectionVariant: (storeId: string, sectionId: string, dto: SwapSectionVariantDto) =>
    http.patch<ApiResponse<WorkspaceState>>(
      ENDPOINTS.themeWorkspace.sectionVariant(storeId, sectionId),
      dto,
    ),

  addSection: (storeId: string, dto: AddSectionDto) =>
    http.post<ApiResponse<WorkspaceState>>(ENDPOINTS.themeWorkspace.sections(storeId), dto),

  removeSection: (storeId: string, sectionId: string) =>
    http.delete<ApiResponse<WorkspaceState>>(
      ENDPOINTS.themeWorkspace.section(storeId, sectionId),
    ),

  reorderSections: (storeId: string, dto: ReorderSectionsDto) =>
    http.patch<ApiResponse<WorkspaceState>>(
      ENDPOINTS.themeWorkspace.reorderSections(storeId),
      dto,
    ),

  confirmProposal: (storeId: string, proposalId: string, dto: ConfirmThemeAssistantProposalDto) =>
    http.post<ApiResponse<ThemeAssistantProposalResult>>(
      ENDPOINTS.themeWorkspace.confirmProposal(storeId, proposalId),
      dto,
    ),

  rejectProposal: (storeId: string, proposalId: string) =>
    http.post<ApiResponse<ThemeAssistantProposalResult>>(
      ENDPOINTS.themeWorkspace.rejectProposal(storeId, proposalId),
    ),

  proposal: (storeId: string, proposalId: string) =>
    http.get<ApiResponse<ThemeAssistantProposalResult>>(
      ENDPOINTS.themeWorkspace.proposal(storeId, proposalId),
    ),

  history: (storeId: string) =>
    http.get<ApiResponse<WorkspaceHistoryState>>(ENDPOINTS.themeWorkspace.history(storeId)),

  undo: (storeId: string) =>
    http.post<ApiResponse<WorkspaceHistoryState>>(ENDPOINTS.themeWorkspace.undo(storeId)),

  redo: (storeId: string) =>
    http.post<ApiResponse<WorkspaceHistoryState>>(ENDPOINTS.themeWorkspace.redo(storeId)),

  revertTo: (storeId: string, snapshotId: string) =>
    http.post<ApiResponse<WorkspaceHistoryState>>(ENDPOINTS.themeWorkspace.revertTo(storeId, snapshotId)),

  /* AI assistant — caller must consume SSE manually */
  sendMessage: (storeId: string, dto: SendMessageDto, signal?: AbortSignal) =>
    fetch(`${import.meta.env.VITE_API_URL}${ENDPOINTS.themeWorkspace.message(storeId)}`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(dto),
    }),

  /* Voice transcription */
  transcribeVoice: (storeId: string, formData: FormData) =>
    http.post<ApiResponse<TranscriptionResult>>(
      ENDPOINTS.themeWorkspace.voiceTranscribe(storeId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ),

  /* Clear AI chat messages */
  clearMessages: (storeId: string, conversationId?: string) =>
    http.delete<ApiResponse<{ success: boolean }>>(
      ENDPOINTS.themeWorkspace.clearMessages(storeId),
      { data: { conversation_id: conversationId } },
    ),
}
